import { getConsent } from "./consent";

/**
 * Anonymous visitor ID for credit tracking.
 *
 *   consent = granted:  FingerprintJS visitorId, cached in a first-party
 *                       cookie (ev_anon_id) so we don't re-fingerprint on
 *                       every page load. Survives cookie clear via FP
 *                       recomputation; survives browser-fingerprint drift
 *                       via the cookie.
 *   consent = denied:   returns null. The backend falls back to an
 *                       IP + User-Agent hash (no client-side storage).
 *   consent = pending:  returns null while the banner is up (treated the
 *                       same as "denied" — we don't persist anything).
 *
 * Resolved once per page load and memoised.
 */

const COOKIE_NAME = "ev_anon_id";
const COOKIE_MAX_AGE_DAYS = 365;

function readCookie(name: string): string | null {
  const row = document.cookie
    .split(";")
    .map((s) => s.trim())
    .find((s) => s.startsWith(name + "="));
  return row ? decodeURIComponent(row.slice(name.length + 1)) : null;
}

function writeCookie(name: string, value: string) {
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
}

function clearCookie(name: string) {
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

let cached: Promise<string | null> | null = null;

async function computeId(): Promise<string | null> {
  if (getConsent() !== "granted") {
    clearCookie(COOKIE_NAME);
    return null;
  }

  const existing = readCookie(COOKIE_NAME);
  if (existing) return existing;

  try {
    const FingerprintJS = await import("@fingerprintjs/fingerprintjs");
    const fp = await FingerprintJS.load();
    const res = await fp.get();
    const id = res.visitorId;
    if (id) {
      writeCookie(COOKIE_NAME, id);
      return id;
    }
  } catch {
    // Fingerprint failed — fall back to a random UUID cookie
  }

  const uuid =
    (window.crypto && "randomUUID" in window.crypto)
      ? window.crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
  writeCookie(COOKIE_NAME, uuid);
  return uuid;
}

export function getAnonId(): Promise<string | null> {
  if (!cached) cached = computeId();
  return cached;
}

/** Re-run when consent changes (granted→denied or vice versa). */
export function resetAnonIdCache() {
  cached = null;
}

window.addEventListener("ev-consent-change", resetAnonIdCache);
